import React, { useEffect, useMemo, useState } from 'react';
import { Button, ButtonToolbar, Modal } from 'reactstrap';
import { KKuTuIOApi } from '../../../KKuTuIOApi';
import { BulkWordNotice, BulkWordSection, CopyButton, ExpandableWordList, FailureList } from './BulkWordCommon';
import './BulkWord.scss';

const BulkWordDeleteConfirmModal = ({
  isOpen,
  lang,
  preview,
  request,
  onToggle,
  onShowToast,
  onComplete
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [isSaving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setConfirmText('');
    setSaving(false);
    setResult(null);
  }, [isOpen, preview]);

  const allText = useMemo(() => {
    if (!preview) return '';
    const lines = [`총 ${preview.totalCount}개의 단어를 삭제합니다.`];
    if (preview.multiThemeWords.length) {
      lines.push('', `2개 이상 주제 단어 ${preview.multiThemeWords.length}개`);
      preview.multiThemeWords.forEach(item => lines.push(`${item.word} <${item.themes.join(', ')}>`));
    }
    preview.themeGroups.filter(group => group.words.length > 0).forEach(group => {
      lines.push('', `${group.themeName} 주제 단어 ${group.words.length}개`, ...group.words);
    });
    if (preview.noThemeWords.length) {
      lines.push('', `주제가 없는 단어 ${preview.noThemeWords.length}개`, ...preview.noThemeWords);
    }
    return lines.join('\n');
  }, [preview]);

  if (!preview) return null;

  const handleDelete = () => {
    if (confirmText !== '삭제' || isSaving || preview.totalCount === 0) return;
    setSaving(true);
    KKuTuIOApi.post(`/words/${lang}/bulk-delete`, request)
      .then(({ data }) => {
        if (!data.success) {
          onShowToast('error', `${data.resultMessage} (${data.resultCode})`);
          setSaving(false);
          return;
        }
        setResult(data.data);
        setSaving(false);
        onShowToast('success', `${data.data.deletedCount}개의 단어를 영구 삭제했습니다.`);
        if (data.data.failures.length === 0) {
          onToggle();
          onComplete();
        }
      })
      .catch(error => {
        setSaving(false);
        onShowToast('error', `대량 삭제 중 오류가 발생했습니다: ${error.message || error}`);
      });
  };

  return (
    <Modal isOpen={isOpen} toggle={onToggle} modalClassName="ltr-support"
           className="modal-dialog--danger modal-dialog--header bulk-word-modal">
      <div className="modal__header">
        <button className="lnr lnr-cross modal__close-btn" type="button" onClick={onToggle}/>
        <h4 className="text-modal modal__title">단어 대량 삭제 - 최종 확인</h4>
      </div>
      <div className="modal__body">
        {result ? (
          <>
            <BulkWordNotice color={result.failures.length ? 'warning' : 'success'}>
              <span>요청 {result.requestedCount}건 중 {result.deletedCount}건을 삭제했습니다.</span>
            </BulkWordNotice>
            <FailureList failures={result.failures}/>
          </>
        ) : (
          <>
            <div className="bulk-word-summary">
              <h4>총 {preview.totalCount}개의 단어를 삭제합니다.</h4>
              <CopyButton text={allText} onCopied={() => onShowToast('success', '전체 내용을 복사했습니다.')}/>
            </div>

            {preview.multiThemeWords.length > 0 && (
              <BulkWordSection title="2개 이상 주제 단어" count={preview.multiThemeWords.length}
                  text={preview.multiThemeWords.map(item => `${item.word} <${item.themes.join(', ')}>`).join('\n')}
                  onCopied={() => onShowToast('success', '복수 주제 단어를 복사했습니다.')}>
                <ExpandableWordList lines={preview.multiThemeWords.map(item => `${item.word} <${item.themes.join(', ')}>` )}/>
              </BulkWordSection>
            )}

            {preview.themeGroups.filter(group => group.words.length > 0).map(group => (
              <BulkWordSection title={`${group.themeName} 단어`} count={group.words.length}
                  key={group.themeCode} text={group.words.join('\n')}
                  onCopied={() => onShowToast('success', `${group.themeName} 단어를 복사했습니다.`)}>
                <ExpandableWordList lines={group.words}/>
              </BulkWordSection>
            ))}

            {preview.noThemeWords.length > 0 && (
              <BulkWordSection title="주제 없는 단어" count={preview.noThemeWords.length}
                  text={preview.noThemeWords.join('\n')}
                  onCopied={() => onShowToast('success', '주제 없는 단어를 복사했습니다.')}>
                <ExpandableWordList lines={preview.noThemeWords}/>
              </BulkWordSection>
            )}

            {preview.duplicateInputWords.length > 0 && (
              <BulkWordNotice color="warning" className="bulk-word-duplicate-notice">
                <span>중복된 단어 {preview.duplicateInputWords.length}개가 제거되었습니다.</span>
              </BulkWordNotice>
            )}
            <FailureList failures={preview.failures}/>

            <BulkWordNotice color="danger" className="bulk-word-danger">
              <strong>동음이의어 하나가 아니라 단어 자체와 모든 뜻을 영구히 삭제합니다. 정말 진행하시겠습니까?</strong>
            </BulkWordNotice>
            <div className="bulk-word-confirm-input">
              <label htmlFor="bulk-delete-confirm">계속하려면 <strong>삭제</strong>를 입력해 주세요.</label>
              <input id="bulk-delete-confirm" name="bulkDeleteConfirm" type="text" value={confirmText}
                     placeholder="삭제" autoComplete="off" onChange={event => setConfirmText(event.target.value)}/>
            </div>
          </>
        )}
      </div>
      <ButtonToolbar className="modal__footer">
        <Button className="modal_cancel" onClick={onToggle} disabled={isSaving}>
          {result ? '닫기' : '작업 취소'}
        </Button>
        {!result && (
          <Button className="modal_ok" color="danger" onClick={handleDelete}
                  disabled={confirmText !== '삭제' || isSaving || preview.totalCount === 0}>
            {isSaving ? '삭제 중...' : '계속'}
          </Button>
        )}
      </ButtonToolbar>
    </Modal>
  );
};

export default BulkWordDeleteConfirmModal;
